import { formatQtyForDisplay } from "./decimalInput";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface StockShortfall {
  ident?: string;
  available?: number | string | null;
  requested?: number | string | null;
  deficit?: number | string | null;
  warehouseName?: string | null;
}

interface MissingIssueRule {
  ident?: string;
  name?: string;
  accountingTypeCode?: string;
}

const missingRuleLine = (row: MissingIssueRule, t: Translate): string => {
  const ident = row?.ident || "";
  const name = row?.name || "";
  const code = typeof row?.accountingTypeCode === "string" ? row.accountingTypeCode.trim() : "";
  return code
    ? t("errors.workOrders.issueRuleMissingLine", { ident, name, code })
    : t("errors.workOrders.issueRuleMissingNoType", { ident, name });
};

const qty = (value: unknown): string => {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "";
  return formatQtyForDisplay(n, { showZero: true });
};

const shortfallLine = (row: StockShortfall, t: Translate): string => {
  const line = t("errors.insufficientStockLine", {
    ident: row?.ident || "",
    available: qty(row?.available),
    requested: qty(row?.requested),
    deficit: qty(row?.deficit),
  });
  const warehouse = typeof row?.warehouseName === "string" ? row.warehouseName.trim() : "";
  return warehouse ? `${line} (${warehouse})` : line;
};

// Stock refusals list each ident. Every other document error stays on the
// same path the RN handlers used before: translated messageKey, then the
// server message, then the caller's fallback.
export const formatRNDocumentError = (
  data: any,
  t: Translate,
  fallbackKey: string,
): string => {
  const shortfalls = Array.isArray(data?.shortfalls) ? data.shortfalls : [];
  if (shortfalls.length > 0) {
    const title = t("errors.insufficientStockMultiple");
    const lines = shortfalls.map((row: StockShortfall) => shortfallLine(row, t));
    return [title, ...lines].join("\n");
  }

  const missingRules = Array.isArray(data?.missingRules) ? data.missingRules : [];
  if (missingRules.length > 0) {
    const titleKey =
      typeof data?.messageKey === "string" && data.messageKey
        ? data.messageKey
        : "errors.workOrders.issueRuleMissing";
    const title = t(titleKey);
    const lines = missingRules.map((row: MissingIssueRule) => missingRuleLine(row, t));
    return [title, ...lines].join("\n");
  }

  const messageKey = typeof data?.messageKey === "string" ? data.messageKey : "";
  if (messageKey) {
    return t(messageKey, {
      existingNumber: data?.existingNumber || "",
      ident: data?.ident || "",
      code: data?.code || "",
    });
  }

  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error) return data.error;
  return t(fallbackKey);
};
